import React, { useState, useCallback, useEffect } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { useNavigation } from "@react-navigation/native";
import { NotFound, SaleRow } from "./components";
import {
  Typography,
  FormControlLabel,
  FormControlGroup,
  AmountBanner,
} from "src/components";
import { useAccount, useSalesHistory } from "src/hooks";
import { dateFormat } from "src/utils/date";
import { SalesType, IdentificationStatus } from "src/domains";
import LocalText from "src/local";
import alert from "src/components/alert";
import { RootStackScreenProps } from "src/navigation/types";

export type SalesHistoryNavigationParam = undefined;

export const SalesHistoryScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"SalesHistory">["navigation"]>();
  const [account] = useAccount();
  const [salesHistoryItems, salesHistoryLoading] = useSalesHistory();
  const [withdrawalLimitDate] = useState<Date>(() => {
    const date = new Date();
    return new Date(date.setMonth(date.getMonth() + 6));
  });

  const withdrawalHandler = useCallback(() => {
    if (!account || account.sales < 500) {
      alert(
        LocalText.screens.salesHistory.amountAlertTitle,
        LocalText.screens.salesHistory.amountAlertDescription
      );
    } else if (account.identificationStatus !== IdentificationStatus.APPROVED) {
      alert(LocalText.screens.salesHistory.identificationAlertTitle);
    } else {
      navigation.push("WithdrawalRequest");
    }
  }, [account]);

  return (
    <View style={styles.root}>
      {!salesHistoryLoading && salesHistoryItems.length === 0 ? (
        <NotFound />
      ) : null}
      {!salesHistoryLoading && salesHistoryItems.length > 0 ? (
        <React.Fragment>
          <React.Fragment>
            {account ? (
              <React.Fragment>
                <AmountBanner
                  containerStyle={styles.banner}
                  text={LocalText.screens.salesHistory.sales}
                  value={account.sales}
                />
                <Typography
                  containerStyle={styles.footer}
                  text={`${LocalText.screens.salesHistory.limit}${dateFormat(
                    withdrawalLimitDate
                  )}`}
                  variant={"section"}
                />
                <FormControlGroup style={styles.formGroup}>
                  <FormControlLabel
                    text={LocalText.screens.salesHistory.withdrawal}
                    rightIcon={"chevron"}
                    onPress={withdrawalHandler}
                  />
                </FormControlGroup>
              </React.Fragment>
            ) : null}
            <Typography
              containerStyle={styles.footer}
              text={LocalText.screens.salesHistory.salesAmountDescription}
              variant={"section"}
            />
            <Typography
              text={LocalText.screens.salesHistory.historySection}
              variant={"section"}
            />
          </React.Fragment>
          <ScrollView style={styles.scrollView}>
            <FormControlGroup>
              {salesHistoryItems.map((item) => (
                <SaleRow
                  key={item.id}
                  {...item}
                  onPress={() => {
                    if (item.type !== SalesType.SALES) {
                      navigation.navigate("Withdrawal", {
                        id: item.id,
                      });
                    }
                  }}
                />
              ))}
            </FormControlGroup>
          </ScrollView>
        </React.Fragment>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.gray1,
  },
  scrollView: {
    flex: 1,
  },
  banner: {
    marginHorizontal: 16,
    marginVertical: 8,
  },
  footer: {
    paddingTop: 0,
  },
  formGroup: {
    marginVertical: 8,
  },
});
